import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type ReactElement,
} from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
    EventType,
    InteractionRequiredAuthError,
    InteractionStatus,
    type EventMessage,
    type PublicClientApplication,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { motion } from "framer-motion";
import type { AppConfig } from "./auth/authConfig";
import { color, motion as motionTokens, usePrefersReducedMotion } from "@tokens/index";
import { motionStyle } from "@primitives/index";
import { SignInGate } from "@compositions/index";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import { createApiClient, InteractionRequiredError } from "./auth/apiClient";
import { ApiClientContext } from "./apiClientContext";
import { WorkflowLayout } from "./WorkflowLayout";
import { AdminLayout } from "./AdminLayout";
import { QueueDetailPage, QueuesIndexPage } from "./QueueAdminPage";
import { RoutingRulesAdminPage } from "./RoutingRulesAdminPage";
import { ManagementGroupsAdminPage } from "./ManagementGroupsAdminPage";
import { NextRankingAdminPage } from "./NextRankingAdminPage";
import { EntityWorkflowsPage } from "./EntityWorkflowsPage";
import { settingsRoutes } from "./settings";
import { ReauthGate } from "./ReauthGate";
import { PresenceChannelBridge, QueueRealtimeBridge, RealtimeProvider } from "./realtime";
import { NotificationNavigationBridge } from "./push";
import { PeerNavigationReceiverBridge } from "./navigation";
import { AppBadgeBridge } from "./app-badge";

const APP_NAME = "Galileo · Einstein";

const mainStyle: CSSProperties = {
    height: "100dvh",
    // Subtle canvas gradient: the page background for the auth gates. Page
    // shells render their own frame over it.
    background: color.gradient.canvas,
    color: color.fg.default,
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
};

// The authenticated tree fades in over the gate it replaces. One opacity
// tween on the incoming side is enough: the gate has nothing to animate
// out, and a presence wrapper here would nest inside the shell's own.
const shellEnterStyle: CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
};
const SHELL_ENTER_SECONDS = 0.2;

// The reauth card covers the shell instead of replacing it. The shell keeps
// its state for the few hundred milliseconds until the redirect navigates
// away; the user sees one card, not a teardown and a rebuild. The layer sits
// above every portal layer (`ux/primitives/overlay/zIndex.ts` tops out at
// the toast, 1200) so an open dialog cannot show through it.
const REAUTH_OVERLAY_Z_INDEX = 1300;
const reauthOverlayStyle: CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: REAUTH_OVERLAY_Z_INDEX,
    display: "flex",
    flexDirection: "column",
    background: color.gradient.canvas,
    color: color.fg.default,
};

export interface ExperienceShellProps {
    readonly msalInstance: PublicClientApplication;
    readonly config: AppConfig;
}

/**
 * Returns true when an error indicates the user must re-authenticate
 * interactively.
 *
 * Two flavors collapse to the same reaction:
 *   1. `InteractionRequiredError` — thrown by our apiClient wrapping any
 *      MSAL silent-acquire failure or "no active account" condition.
 *   2. Raw `InteractionRequiredAuthError` — raised by MSAL itself outside
 *      our apiClient (e.g. from an event subscription).
 *
 * Both surface as the same UX: redirect the user to Microsoft via
 * `<ReauthGate>`. Compositions never need to distinguish.
 */
function isInteractionRequired(error: unknown): boolean {
    return (
        error instanceof InteractionRequiredError || error instanceof InteractionRequiredAuthError
    );
}

export function ExperienceShell({ msalInstance, config }: ExperienceShellProps): ReactElement {
    // `reauthRequired` is the single source of truth for "we lost the session,
    // we're sending the user back to Microsoft." It flips on:
    //   - any react-query query error that is InteractionRequiredError
    //   - any react-query mutation error that is InteractionRequiredError
    //   - any MSAL ACQUIRE_TOKEN_FAILURE event with that error class
    // Once true, ReauthGate mounts, fires acquireTokenRedirect, and the
    // browser navigates to Microsoft. The successful round-trip lands the
    // user back at the app with a fresh token; this component remounts and
    // the flag resets to false.
    const [reauthRequired, setReauthRequired] = useState(false);

    // The flag callback is stable across renders so the QueryClient created
    // below is stable too. This matters: a new QueryClient on every render
    // would discard cached data on every parent re-render.
    const flagReauthRequired = useCallback(() => {
        setReauthRequired(true);
    }, []);

    // Latest-callback ref so the QueryClient's cache callbacks (created once)
    // always see the freshest setter. Avoids the "stale closure" trap without
    // recreating the QueryClient.
    const flagRef = useRef(flagReauthRequired);
    useEffect(() => {
        flagRef.current = flagReauthRequired;
    }, [flagReauthRequired]);

    const apiClient = useMemo(
        () => createApiClient({ instance: msalInstance, config }),
        [msalInstance, config],
    );

    // QueryClient is created exactly once per shell mount. Its caches own the
    // boundary between "domain error" (passes through to the composition's
    // error UI) and "auth error" (intercepted, gate raised, redirect fired).
    const queryClient = useMemo(() => {
        const handleCacheError = (error: unknown): void => {
            if (isInteractionRequired(error)) {
                flagRef.current();
            }
        };
        return new QueryClient({
            queryCache: new QueryCache({ onError: handleCacheError }),
            mutationCache: new MutationCache({ onError: handleCacheError }),
            defaultOptions: {
                queries: {
                    retry: (failureCount, error) => {
                        // Don't retry interaction-required errors — the only
                        // meaningful response is a redirect, which the gate
                        // handles. Retrying spams MSAL with silent-acquire
                        // attempts that will all fail identically.
                        if (isInteractionRequired(error)) return false;
                        return failureCount < 1;
                    },
                    // A scene switch remounts its observers. With staleTime 0
                    // every remount refetches and the surface re-paints even
                    // when the cache is seconds old. 30s matches the explicit
                    // window most feature hooks already declare; realtime
                    // invalidation stays the path for "something changed".
                    staleTime: 30_000,
                    refetchOnWindowFocus: false,
                },
            },
        });
    }, []);

    // Subscribe to MSAL events for token failures that happen outside our
    // apiClient — silent renewal on idle, account-changed events, etc. This
    // closes the gap where a token expires while the user is reading the
    // page (no react-query call in flight) so we still catch it as soon as
    // MSAL notices.
    useEffect(() => {
        const callbackId = msalInstance.addEventCallback((event: EventMessage) => {
            if (event.eventType !== EventType.ACQUIRE_TOKEN_FAILURE) return;
            if (isInteractionRequired(event.error)) {
                flagRef.current();
            }
        });
        return () => {
            if (callbackId !== null) {
                msalInstance.removeEventCallback(callbackId);
            }
        };
    }, [msalInstance]);

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider instance={msalInstance} config={config}>
                <ApiClientContext.Provider value={apiClient}>
                    <div style={mainStyle}>
                        <AuthGate
                            msalInstance={msalInstance}
                            config={config}
                            onInteractionRequired={flagReauthRequired}
                        />
                        {reauthRequired && (
                            <div style={reauthOverlayStyle} data-reauth-overlay="">
                                <ReauthGate
                                    instance={msalInstance}
                                    config={config}
                                    appName={APP_NAME}
                                />
                            </div>
                        )}
                    </div>
                </ApiClientContext.Provider>
            </AuthProvider>
        </QueryClientProvider>
    );
}

interface AuthGateProps {
    readonly msalInstance: PublicClientApplication;
    readonly config: AppConfig;
    readonly onInteractionRequired: () => void;
}

/**
 * Decides between the unauthenticated and authenticated compositions.
 *
 * This lives inside experience-shell (not in compositions) because the
 * decision itself is auth-aware — compositions must stay ignorant of auth
 * internals to remain portable. The third state — interaction-required —
 * is owned by the parent ExperienceShell so this gate can stay focused on
 * the binary "do you have an account or not" question.
 *
 * The realtime connection only makes sense when authenticated, so the
 * `RealtimeProvider` mounts inside the authenticated branch. A sign-out
 * (or any other tear-down that drops the account) unmounts the provider
 * and the underlying SignalR connection stops cleanly with the React
 * lifecycle.
 *
 * Boot order matters here. `bootstrapAuth` resolves the active account
 * before React mounts, so `auth.account` is already set on the first
 * render; msal-react's own `inProgress` still starts at `Startup` and its
 * `useIsAuthenticated` reports false until an effect catches up. The gate
 * therefore keys on the account, not on `isAuthenticated`, and while MSAL
 * is still in `Startup` / `HandleRedirect` with no account it renders
 * nothing rather than a sign-in card that would be wrong three frames
 * later. `AcquireToken` is deliberately not treated as "busy": silent
 * acquires run on every API call.
 */
function AuthGate({ msalInstance, config, onInteractionRequired }: AuthGateProps): ReactElement | null {
    const auth = useAuth();
    const { inProgress } = useMsal();
    const reduced = usePrefersReducedMotion();
    const hasAccount = auth.account !== null;
    // Captured once: a shell that mounts already signed in has nothing to
    // fade in over; only the interactive sign-in -> shell hand-off animates.
    const mountedAuthenticatedRef = useRef(hasAccount);

    if (!hasAccount) {
        const booting =
            inProgress === InteractionStatus.Startup ||
            inProgress === InteractionStatus.HandleRedirect;
        if (booting) {
            return null;
        }
        return (
            <SignInGate
                appName={APP_NAME}
                onSignIn={() => {
                    void auth.signIn();
                }}
            />
        );
    }

    return (
        <motion.div
            key="shell"
            data-shell-enter=""
            style={motionStyle(shellEnterStyle)}
            initial={mountedAuthenticatedRef.current ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
                duration: reduced ? motionTokens.reduced.duration : SHELL_ENTER_SECONDS,
                ease: motionTokens.curves.standard,
            }}
        >
            <RealtimeProvider
                instance={msalInstance}
                config={config}
                onInteractionRequired={onInteractionRequired}
            >
                {/*
                 * App-wide realtime subscriptions live as siblings of <Routes>
                 * so they survive route transitions and are mounted exactly
                 * once per authenticated session. Any page that renders queue
                 * tallies (sidebar badges, the admin tree, future dashboards)
                 * inherits live updates from the bridge without remembering
                 * to mount the hook locally.
                 */}
                <QueueRealtimeBridge />
                <PresenceChannelBridge />
                {/*
                 * Taskbar App Badge: reflects the user's outstanding work item
                 * count onto the installed PWA icon. Mounted here so its
                 * count query stays subscribed across every route and inherits
                 * the same realtime `["workitems","mine"]` invalidations.
                 */}
                <AppBadgeBridge />
                {/*
                 * Web Push notification-click navigation: the service worker
                 * focuses this tab and posts the in-app route; this bridge turns
                 * that into a client-side React Router navigation. Mounted here so
                 * it has a router context and survives route transitions.
                 */}
                <NotificationNavigationBridge />
                {/*
                 * Cross-app navigation (Galileo -> Einstein): Galileo relays a
                 * navigation intent over SignalR; the installed Einstein PWA (which
                 * auto-designates itself as the recipient) handles it, navigating in
                 * place and acking so Galileo suppresses its window.open fallback.
                 * Mounted here for router context and one-per-session designation,
                 * alongside the other realtime bridges.
                 */}
                <PeerNavigationReceiverBridge />
                <Routes>
                    {/*
                     * One route for the workspace and the open workflow. The
                     * optional segment keeps a single `WorkflowLayout` element
                     * across `/workflows` <-> `/workflows/:id`, so the layout,
                     * its dock, dialogs, and shortcuts mount once and the scene
                     * switch inside it is a crossfade, not a remount.
                     */}
                    <Route path="/workflows/:id?" element={<WorkflowLayout />} />
                    <Route
                        path="/entities/:entityType/:entityId"
                        element={<EntityWorkflowsPage />}
                    />
                    {/*
                     * Admin area shares one chrome + nav bar via AdminLayout.
                     * Pages render through <Outlet/> and own their own
                     * content-width container (widths intentionally differ
                     * per page).
                     */}
                    <Route element={<AdminLayout />}>
                        <Route path="/queues" element={<QueuesIndexPage />} />
                        <Route path="/queues/:id" element={<QueueDetailPage />} />
                        <Route path="/routing-rules" element={<RoutingRulesAdminPage />} />
                        <Route path="/management-groups" element={<ManagementGroupsAdminPage />} />
                        <Route path="/next-ranking" element={<NextRankingAdminPage />} />
                    </Route>
                    {/*
                     * Per-user settings: own layout, not under AdminLayout. The
                     * index redirects to the first category on medium+ and is
                     * the category list on compact.
                     */}
                    {settingsRoutes()}
                    <Route path="*" element={<Navigate to="/workflows" replace />} />
                </Routes>
            </RealtimeProvider>
        </motion.div>
    );
}
